<template>
  <transition name="modal-fade">
    <div v-if="visible" class="redemption-modal-overlay" @click.self="handleClose">
      <div class="redemption-modal-container">
        <!-- HEADER -->
        <div class="redemption-header">
          <div class="redemption-logo">
            <img src="@/assets/static/index/tcc_logo_timeclub.png" alt="TIME Club" />
          </div>
          <div class="redemption-headline">
            <h2>{{ $t('redemption_modal.title') }}</h2>
            <p>{{ $t('redemption_modal.desc') }}</p>
          </div>
          <button class="redemption-close-btn" @click="handleClose" :aria-label="$t('common.close')">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- NAV BOX -->
        <div class="redemption-nav">
          <button
            :class="['nav-item', { active: activeTab === 'item' }]"
            @click="activeTab = 'item'"
          >
            {{ $t('redemption_modal.tabs.item') }}
          </button>
          <button
            :class="['nav-item', { active: activeTab === 'coupon' }]"
            @click="activeTab = 'coupon'"
          >
            {{ $t('redemption_modal.tabs.coupon') }}
          </button>
          <button
            :class="['nav-item', { active: activeTab === 'free-shipping' }]"
            @click="activeTab = 'free-shipping'"
          >
            {{ $t('redemption_modal.tabs.free_shipping') }}
          </button>
        </div>

        <!-- CONTENT -->
        <div class="redemption-content">
          <!-- 禮品列表 -->
          <div v-show="activeTab === 'item'" class="redemption-list">
            <div v-if="loading" class="redemption-loading">
              <span class="loading-spinner"></span>
            </div>
            <div v-else-if="items.length === 0" class="redemption-empty">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 7h-9M14 17H5M17 17a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM7 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z"/></svg>
              <p>{{ $t('redemption_modal.no_items') }}</p>
            </div>
            <div v-else class="redemption-grid">
              <div
                v-for="item in items"
                :key="item.id"
                class="redemption-item-card"
              >
                <div class="item-thumb">
                  <img :src="item.image" :alt="item.name" />
                </div>
                <div class="item-content">
                  <div class="item-info">
                    <div class="item-name">
                      <div v-if="item.brand" class="item-brand">{{ item.brand }}</div>
                      <div class="item-title">{{ item.name }}</div>
                    </div>
                  </div>
                  <div class="item-action">
                    <div class="item-points">
                      {{ item.points }}<span>{{ $t('redemption_modal.points') }}</span>
                    </div>
                    <button
                      class="item-redeem-btn"
                      :class="{ disabled: userPoints < item.points || item.soldOut || redeeming }"
                      :disabled="userPoints < item.points || item.soldOut || redeeming"
                      @click="handleRedeem(item, 'goods')"
                    >
                      {{ item.soldOut ? $t('redemption_modal.sold_out') : (userPoints < item.points ? $t('redemption_modal.need_more', { points: item.points - userPoints }) : $t('redemption_modal.redeem_now')) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              :current-page="itemPage"
              :total-items="itemTotalCount"
              :page-size="pageSize"
              @page-change="handlePageChange"
            />
          </div>

          <!-- 現金券列表 -->
          <div v-show="activeTab === 'coupon'" class="redemption-list">
            <div v-if="loading" class="redemption-loading">
              <span class="loading-spinner"></span>
            </div>
            <div v-else-if="coupons.length === 0" class="redemption-empty">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
              <p>{{ $t('redemption_modal.no_coupons') }}</p>
            </div>
            <div v-else class="redemption-grid">
              <div
                v-for="coupon in coupons"
                :key="coupon.id"
                class="redemption-item-card"
              >
                <div class="coupon-content">
                  <div class="coupon-value">
                    <span class="coupon-currency">USD$</span>
                    <span class="coupon-amount">{{ coupon.value }}</span>
                  </div>
                  <div class="coupon-info">
                    <p class="coupon-name">{{ coupon.name }}</p>
                    <!-- 特定商品/品牌标签 -->
                    <span v-if="coupon.coupon_type === '2'" class="coupon-type-tag coupon-type-product">{{ $t('redemption_modal.type_specific_product') }}</span>
                    <span v-else-if="coupon.coupon_type === '3'" class="coupon-type-tag coupon-type-brand">{{ $t('redemption_modal.type_specific_brand') }}</span>
                    <p v-if="coupon.coupon_type === '2' && coupon.goodsinfo && coupon.goodsinfo.length > 0" class="coupon-applicable">{{ $t('redemption_modal.applicable_products') }}: {{ formatInfoList(coupon.goodsinfo) }}</p>
                    <p v-else-if="coupon.coupon_type === '3' && coupon.brandinfo && coupon.brandinfo.length > 0" class="coupon-applicable">{{ $t('redemption_modal.applicable_brands') }}: {{ formatInfoList(coupon.brandinfo) }}</p>
                    <p v-if="coupon.minSpend > 0" class="coupon-desc">{{ $t('redemption_modal.min_spend', { amount: coupon.minSpend }) }}</p>
                    <p v-else class="coupon-desc">{{ $t('redemption_modal.coupon_desc') }}</p>
                  </div>
                  <div class="item-action">
                    <div class="item-points">
                      {{ coupon.points }}<span>{{ $t('redemption_modal.points') }}</span>
                    </div>
                    <button
                      class="item-redeem-btn"
                      :class="{ disabled: userPoints < coupon.points || coupon.soldOut || redeeming }"
                      :disabled="userPoints < coupon.points || coupon.soldOut || redeeming"
                      @click="handleRedeem(coupon, 'coupon')"
                    >
                      {{ coupon.soldOut ? $t('redemption_modal.sold_out') : (userPoints < coupon.points ? $t('redemption_modal.need_more', { points: coupon.points - userPoints }) : $t('redemption_modal.redeem_now')) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              :current-page="couponPage"
              :total-items="couponTotalCount"
              :page-size="pageSize"
              @page-change="handlePageChange"
            />
          </div>

          <!-- 免運費券列表 -->
          <div v-show="activeTab === 'free-shipping'" class="redemption-list">
            <div v-if="loading" class="redemption-loading">
              <span class="loading-spinner"></span>
            </div>
            <div v-else-if="freeCoupons.length === 0" class="redemption-empty">
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
              <p>{{ $t('redemption_modal.no_free_coupons') }}</p>
            </div>
            <div v-else class="redemption-grid">
              <div
                v-for="coupon in freeCoupons"
                :key="coupon.id"
                class="redemption-item-card"
              >
                <div class="coupon-content">
                  <div class="coupon-value coupon-value-free">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
                    <span class="coupon-free-label">{{ $t('redemption_modal.free_shipping_label') }}</span>
                  </div>
                  <div class="coupon-info">
                    <p class="coupon-name">{{ coupon.name }}</p>
                    <p v-if="coupon.minSpend > 0" class="coupon-desc">{{ $t('redemption_modal.min_spend', { amount: coupon.minSpend }) }}</p>
                    <p v-else class="coupon-desc">{{ $t('redemption_modal.coupon_desc') }}</p>
                  </div>
                  <div class="item-action">
                    <div class="item-points">
                      {{ coupon.points }}<span>{{ $t('redemption_modal.points') }}</span>
                    </div>
                    <button
                      class="item-redeem-btn"
                      :class="{ disabled: userPoints < coupon.points || coupon.soldOut || redeeming }"
                      :disabled="userPoints < coupon.points || coupon.soldOut || redeeming"
                      @click="handleRedeem(coupon, 'coupon')"
                    >
                      {{ coupon.soldOut ? $t('redemption_modal.sold_out') : (userPoints < coupon.points ? $t('redemption_modal.need_more', { points: coupon.points - userPoints }) : $t('redemption_modal.redeem_now')) }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <Pagination
              :current-page="freePage"
              :total-items="freeTotalCount"
              :page-size="pageSize"
              @page-change="handlePageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { mapActions } from 'vuex'
import { getFullUrl } from '@/api/request'
import Pagination from './Pagination.vue'

export default {
  name: 'RedemptionModal',
  components: { Pagination },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    userPoints: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      activeTab: 'item',
      apiItems: null,
      apiCoupons: null,
      apiFreeCoupons: null,
      loading: false,
      redeeming: false,
      pageSize: 12,
      itemPage: 1,
      itemTotalCount: 0,
      couponPage: 1,
      couponTotalCount: 0,
      freePage: 1,
      freeTotalCount: 0
    }
  },
  computed: {
    isEn() {
      return this.$i18n ? this.$i18n.locale === 'en' : false
    },
    items() {
      if (!this.apiItems) return []
      return this.apiItems.map(g => ({
        id: g.id,
        type: 'goods',
        brand: '',
        name: this.isEn ? (g.title_en || g.title) : (g.title || g.title_en),
        image: getFullUrl(g.image),
        points: g.score,
        stock: g.stock,
        soldOut: g.is_over === 1 || g.stock === 0
      }))
    },
    coupons() {
      if (!this.apiCoupons) return []
      return this.apiCoupons.map(c => this.mapCoupon(c))
    },
    freeCoupons() {
      if (!this.apiFreeCoupons) return []
      return this.apiFreeCoupons.map(c => ({
        id: c.id,
        type: 'free-shipping',
        name: this.isEn ? (c.name_en || c.name) : (c.name || c.name_en),
        value: 0,
        minSpend: 0,
        points: c.score || 0,
        soldOut: c.is_over === 1,
        counts: c.counts || 0
      }))
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.loadAll()
      }
    }
  },
  methods: {
    ...mapActions(['fetchScoreGoods', 'fetchScoreCoupons', 'fetchFreeCoupons', 'exchangeGoods', 'exchangeCoupon', 'showToast']),
    mapCoupon(c) {
      return {
        id: c.id,
        type: 'coupon',
        name: this.isEn ? (c.name_en || c.name) : (c.name || c.name_en),
        value: c.money,
        minSpend: parseFloat(c.basepay) || 0,
        points: c.score,
        soldOut: c.is_over === 1,
        coupon_type: String(c.type || c.coupon_type || '1'),
        goodsinfo: Array.isArray(c.goodsinfo) ? c.goodsinfo : [],
        brandinfo: Array.isArray(c.brandinfo) ? c.brandinfo : []
      }
    },
    formatInfoList(list) {
      if (!Array.isArray(list) || list.length === 0) return ''
      return list.map(item => this.isEn ? (item.name_en || item.title_en || item.name || item.title) : (item.name || item.title || item.name_en || item.title_en)).join(', ')
    },
    async loadAll() {
      this.loading = true
      try {
        const [itemsRes, couponsRes, freeRes] = await Promise.all([
          this.fetchScoreGoods({ page: this.itemPage, count: this.pageSize }),
          this.fetchScoreCoupons({ page: this.couponPage, count: this.pageSize }),
          this.fetchFreeCoupons({ page: this.freePage, count: this.pageSize })
        ])
        this.apiItems = itemsRes.list
        this.itemTotalCount = itemsRes.totalCount
        this.apiCoupons = couponsRes.list
        this.couponTotalCount = couponsRes.totalCount
        this.apiFreeCoupons = freeRes.list
        this.freeTotalCount = freeRes.totalCount
      } catch (e) {
        console.error('加載兌換列表失敗', e)
      } finally {
        this.loading = false
      }
    },
    async handlePageChange(page) {
      this.loading = true
      try {
        if (this.activeTab === 'item') {
          this.itemPage = page
          const res = await this.fetchScoreGoods({ page, count: this.pageSize })
          this.apiItems = res.list
          this.itemTotalCount = res.totalCount
        } else if (this.activeTab === 'coupon') {
          this.couponPage = page
          const res = await this.fetchScoreCoupons({ page, count: this.pageSize })
          this.apiCoupons = res.list
          this.couponTotalCount = res.totalCount
        } else {
          this.freePage = page
          const res = await this.fetchFreeCoupons({ page, count: this.pageSize })
          this.apiFreeCoupons = res.list
          this.freeTotalCount = res.totalCount
        }
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.$emit('close')
    },
    async handleRedeem(item, type) {
      if (this.redeeming) return
      if (this.userPoints < item.points) return
      if (item.soldOut) return
      this.redeeming = true
      try {
        let success = false
        if (type === 'goods') {
          success = await this.exchangeGoods(item.id)
          if (success) {
            const res = await this.fetchScoreGoods({ page: this.itemPage, count: this.pageSize })
            this.apiItems = res.list
            this.itemTotalCount = res.totalCount
          }
        } else {
          success = await this.exchangeCoupon(item.id)
          if (success) {
            const [couponsRes, freeRes] = await Promise.all([
              this.fetchScoreCoupons({ page: this.couponPage, count: this.pageSize }),
              this.fetchFreeCoupons({ page: this.freePage, count: this.pageSize })
            ])
            this.apiCoupons = couponsRes.list
            this.couponTotalCount = couponsRes.totalCount
            this.apiFreeCoupons = freeRes.list
            this.freeTotalCount = freeRes.totalCount
          }
        }
        if (success) {
          this.$emit('redeem', item)
        }
      } finally {
        this.redeeming = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.redemption-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  overflow-y: auto;
}

.redemption-modal-container {
  background: #fff;
  width: 100%;
  max-width: 1200px;
  max-height: 90vh;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* HEADER */
.redemption-header {
  background: #f8f6f0;
  padding: 30px 40px;
  display: flex;
  align-items: center;
  gap: 24px;
  border-bottom: 1px solid #e5dcc5;
  position: relative;
}

.redemption-logo {
  flex-shrink: 0;

  img {
    height: 60px;
    width: auto;
  }
}

.redemption-headline {
  flex: 1;

  h2 {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 8px 0;
  }

  p {
    font-size: 14px;
    color: #666;
    margin: 0;
    line-height: 1.6;
  }
}

.redemption-close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: #666;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.3s;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #333;
  }
}

/* NAV */
.redemption-nav {
  display: flex;
  border-bottom: 2px solid #b9aa6b;
  padding: 0 40px;
  background: #fff;
}

.nav-item {
  padding: 12px 24px;
  border: none;
  background: none;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;

  &:hover {
    color: #333;
  }

  &.active {
    color: #b9aa6b;
    font-weight: 600;

    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: #b9aa6b;
    }
  }
}

/* CONTENT */
.redemption-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 30px 40px;
}

.redemption-list {
  display: flex;
  flex-direction: column;
  min-height: 400px;
}

.redemption-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

/* LOADING */
.redemption-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e5e5e5;
  border-top-color: #b9aa6b;
  border-radius: 50%;
  animation: redemption-spin 0.8s linear infinite;
}

@keyframes redemption-spin {
  to { transform: rotate(360deg); }
}

/* EMPTY STATE */
.redemption-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  color: #ccc;

  svg {
    color: #ddd;
    margin-bottom: 16px;
  }

  p {
    font-size: 15px;
    color: #999;
    margin: 0;
  }
}

/* ITEM CARD */
.redemption-item-card {
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }
}

.item-thumb {
  width: 100%;
  aspect-ratio: 1;
  background: #f8f8f8;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.item-content {
  padding: 16px;
}

.item-info {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.item-name {
  flex: 1;

  .item-brand {
    font-size: 12px;
    color: #999;
    margin-bottom: 4px;
  }

  .item-title {
    font-size: 14px;
    color: #333;
    font-weight: 500;
    line-height: 1.4;
  }
}

.item-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.item-points {
  font-size: 20px;
  font-weight: 600;
  color: #b9aa6b;

  span {
    font-size: 12px;
    margin-left: 2px;
  }
}

.item-redeem-btn {
  flex: 1;
  padding: 8px 16px;
  border: none;
  background: #333;
  color: #fff;
  font-size: 13px;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;

  &:hover:not(.disabled) {
    background: #1a1a1a;
  }

  &.disabled {
    background: #999;
    cursor: not-allowed;
  }
}

/* COUPON CARD */
.coupon-content {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 200px;
}

.coupon-value {
  text-align: center;
  padding: 20px 0;
  border-bottom: 2px dashed #e5e5e5;

  .coupon-currency {
    font-size: 18px;
    color: #666;
    margin-right: 4px;
    display: inline-block;
    padding-right: 2px;
  }

  .coupon-amount {
    font-size: 48px;
    font-weight: 700;
    color: #b9aa6b;
  }
}

.coupon-value-free {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  svg {
    color: #b9aa6b;
  }

  .coupon-free-label {
    font-size: 22px;
    font-weight: 700;
    color: #b9aa6b;
    letter-spacing: 1px;
  }
}

.coupon-info {
  flex: 1;

  .coupon-name {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
  }

  .coupon-type-tag {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    margin-bottom: 6px;

    &.coupon-type-product {
      background: #e3f2fd;
      color: #1565c0;
    }

    &.coupon-type-brand {
      background: #f3e5f5;
      color: #7b1fa2;
    }
  }

  .coupon-applicable {
    font-size: 12px;
    color: #666;
    margin: 0 0 6px 0;
  }

  .coupon-desc {
    font-size: 13px;
    color: #666;
    margin: 0;
  }
}

/* MODAL TRANSITIONS */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* RESPONSIVE */
@media (max-width: 1024px) {
  .redemption-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .redemption-header {
    padding: 20px;
    flex-wrap: wrap;
  }

  .redemption-logo img {
    height: 40px;
  }

  .redemption-headline h2 {
    font-size: 18px;
  }

  .redemption-headline p {
    font-size: 13px;
  }

  .redemption-nav {
    padding: 0 20px;
  }

  .nav-item {
    padding: 12px 16px;
    font-size: 14px;
  }

  .redemption-content {
    padding: 20px;
  }

  .redemption-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .redemption-list,
  .redemption-loading,
  .redemption-empty {
    min-height: 300px;
  }
}
</style>
